import React, { useState } from 'react';
import { Link2, Loader2, Wrench, Globe, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../lib/apiError';

const Row = ({ children }) => <div className="flex gap-2 py-0.5 border-t items-baseline" style={{ borderColor: 'rgba(0,0,0,0.05)' }}>{children}</div>;

function BrokenList({ res }) {
  if (!res) return null;
  return (
    <div className="mt-2" data-testid="link-check-result">
      <b style={{ color: res.broken_count ? '#8a1f11' : 'var(--navy)' }}>
        Visited {res.checked} web address{res.checked !== 1 ? 'es' : ''} — {res.broken_count ? `${res.broken_count} no longer load` : 'all load correctly'}
      </b>
      {res.broken.map((b, n) => (
        <Row key={n}>
          <span className="w-36 truncate" title={b.used_in.map((u) => `${u.where}: ${u.name}`).join('\n')}>{b.used_in[0].where}: {b.used_in[0].name}{b.used_in.length > 1 ? ` +${b.used_in.length - 1}` : ''}</span>
          <a href={b.url} target="_blank" rel="noopener noreferrer" className="truncate inline-flex items-center gap-1" style={{ color: '#8a1f11' }} data-testid={`link-broken-${n}`}>{b.url} <ExternalLink size={10} /></a>
          <span className="ml-auto whitespace-nowrap" style={{ color: 'var(--olive)' }}>{b.reason}</span>
        </Row>
      ))}
      {res.redirected.length > 0 && <p className="mt-1" style={{ color: 'var(--olive)' }}>{res.redirected.length} address{res.redirected.length !== 1 ? 'es' : ''} now redirect elsewhere (still load).</p>}
    </div>
  );
}

// Audit every stored hyperlink (articles, pages, templates): repair bad syntax, then visit each web address.
export default function LinkAuditButton() {
  const { linksAudit, linksFix, linksCheck } = useAuth();
  const [busy, setBusy] = useState('');
  const [res, setRes] = useState(null);
  const [check, setCheck] = useState(null);

  const run = async (fix) => {
    setBusy(fix ? 'fix' : 'audit');
    try {
      const r = fix ? await linksFix() : await linksAudit();
      setRes({ ...r, fixed: fix });
      if (fix) toast.success(`Repaired links in ${r.fixed_documents} item(s); ${r.remaining} still need attention.`);
      else toast[r.count ? 'warning' : 'success'](r.count ? `${r.count} link(s) have incorrect syntax.` : 'All stored links have correct syntax.');
    } catch (e) { toast.error(formatApiError(e, 'Could not check links.')); }
    setBusy('');
  };

  const visit = async () => {
    setBusy('visit');
    try {
      const r = await linksCheck();
      setCheck(r);
      toast[r.broken_count ? 'warning' : 'success'](r.broken_count ? `${r.broken_count} web address(es) no longer load.` : `All ${r.checked} web addresses load.`);
    } catch (e) { toast.error(formatApiError(e, 'Could not visit the links.')); }
    setBusy('');
  };

  const issues = res ? res.issues : [];
  return (
    <span className="inline-block mb-3 ml-2 align-top">
      <button onClick={() => run(false)} disabled={!!busy} data-testid="link-audit-btn" className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-title border" style={{ borderColor: 'var(--navy)', color: 'var(--navy)' }}>
        {busy === 'audit' ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />} Check links
      </button>
      {(res || check) && (
        <div className="mt-2 rounded border bg-white p-3 text-xs max-w-2xl" style={{ borderColor: 'rgba(0,0,0,0.1)' }} data-testid="link-audit-result">
          {res && (
            <>
              <div className="flex items-center justify-between gap-2 mb-1">
                <b style={{ color: 'var(--navy)' }}>{res.fixed ? `Repaired ${res.fixed_documents} item(s) — ${res.remaining} remaining` : `${res.count} link(s) with incorrect syntax`}</b>
                <span className="flex gap-1">
                  {!res.fixed && res.count > 0 && <button onClick={() => run(true)} disabled={!!busy} data-testid="link-fix-btn" className="inline-flex items-center gap-1 px-2 py-1 rounded font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}><Wrench size={12} /> Fix all</button>}
                  <button onClick={visit} disabled={!!busy} data-testid="link-visit-btn" title="Open every web address and report any that no longer load" className="inline-flex items-center gap-1 px-2 py-1 rounded font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }}>
                    {busy === 'visit' ? <Loader2 size={12} className="animate-spin" /> : <Globe size={12} />} Visit addresses
                  </button>
                </span>
              </div>
              {issues.slice(0, 20).map((i, n) => (
                <Row key={n}>
                  <span className="w-36 truncate" title={i.name}>{i.where}: {i.name}</span>
                  <code className="truncate" style={{ color: '#8a1f11' }}>{i.href}</code>
                  <span>→</span>
                  <code className="truncate" style={{ color: '#1c6b2c' }}>{i.fixed}</code>
                </Row>
              ))}
              {issues.length === 0 && <p style={{ color: 'var(--olive)' }}>Links in articles, pages and templates all use a full address (https://, mailto:, tel: or a site path).</p>}
              {busy === 'visit' && <p className="mt-1" style={{ color: 'var(--olive)' }}>Visiting each web address — this can take a minute…</p>}
            </>
          )}
          <BrokenList res={check} />
        </div>
      )}
    </span>
  );
}
